import { useState } from 'react';
import type { ApiClient, IframePoolOrderItem, IframePoolShipping } from '../../../modules/api';
import { formatPrice } from '../../../modules/currency';
import { HighValueDisclaimer, ProtectionOptOutWarning } from '../../../modules/shipping';
import { SleekButton } from '../../../modules/theme';
import { useBackHandler } from '../../../modules/backStack';
import { useShipmentDraft } from '../../../pages/Delivery';
import { SLEEK_PAGE_MIN_HEIGHT, SleekPageHeader } from '../shared';
import { VaultCardRow } from '../MyPacks/cards/VaultCardRow';
import { ShipSection } from './ShipSection';
import { ShippingOptionList } from './ShippingOptionList';
import { PaymentSummary } from './PaymentSummary';
import { AddressLinesText } from './AddressLinesText';
import { AddressFormPage } from './AddressFormPage';
import { ShipCardFeedback } from './ShipCardFeedback';

type Props = {
  client: ApiClient;
  /** The one card being shipped. */
  item: IframePoolOrderItem;
  onBack: () => void;
  onCreated: (shipping: IframePoolShipping) => void;
};

const DISCLAIMER =
  'We want to be fully transparent with our users and make it clear that if you choose shipping, ' +
  'you will need to pay a shipping & handling fee.';
const PRICE_DECIMALS = 2;

/**
 * Ship one Vault card (partner Figma 2026-10-08) — no disclaimer sheet in
 * front of it: the disclaimer, the player's single address (add / edit on its
 * own form), the delivery options, the card and the payment, then "Confirm
 * shipment • $X", lit once there is an address and a picked option.
 */
export function ShipCardPage({ client, item, onBack, onCreated }: Props) {
  const draft = useShipmentDraft(client, [item], { preselectFirstService: false, onCreated });
  const [isEditingAddress, setIsEditingAddress] = useState(false);
  const { selected: address, loading: addressLoading, addCreated, updateSaved } = draft.addresses;
  const { shippingRowValue, showInsuranceRow, insuranceRowValue, handlingFee, salesTax, total, promo } = draft.summary;
  const hasService = draft.selectedServiceId !== null;
  const canConfirm = draft.canSubmit && hasService && !draft.quotaFull;
  // Handling alone isn't what the player pays — no total until an option is
  // picked and its shipping is quoted.
  const shownTotal = hasService ? total : null;
  // The address (or the short "Add address" button without one) and the
  // option list set the page's height: hold everything under the disclaimer
  // until both land, so it appears once instead of jumping down twice.
  const isLoading = addressLoading || draft.servicesLoading;

  useBackHandler(isEditingAddress, () => setIsEditingAddress(false), 'overlay');

  if (isEditingAddress) {
    return (
      <AddressFormPage
        client={client}
        address={address}
        onBack={() => setIsEditingAddress(false)}
        onSaved={(saved) => {
          setIsEditingAddress(false);
          if (!address) {
            addCreated(saved);
            return;
          }
          // An edit keeps the address id, which is all the quote is keyed on:
          // re-quote by hand, or a fixed ZIP keeps the old price (or the old
          // "can't rate this address" error, with Confirm locked).
          updateSaved(saved);
          draft.fees.retry();
        }}
      />
    );
  }

  const editLink = address && (
    <button type="button" onClick={() => setIsEditingAddress(true)} className="text-[14px] font-semibold leading-[20px] tracking-[-0.15px] text-[var(--co-ShipCard-linkColor,#C084FC)]">
      Edit address
    </button>
  );
  const confirmLabel = shownTotal == null ? 'Confirm shipment' : `Confirm shipment • ${formatPrice(shownTotal, PRICE_DECIMALS)}`;

  return (
    <div data-page="ship-card" data-co-component="ShipCard" className={`@container relative flex ${SLEEK_PAGE_MIN_HEIGHT} w-full flex-col`}>
      <SleekPageHeader onBack={onBack} title="Ship Card" />
      <main className="mx-auto w-full max-w-[480px] flex-1">
        <ShipSection title="Disclaimer">
          <p className="m-0 text-[14px] leading-[20px] tracking-[-0.15px] text-[var(--co-ShipCard-mutedText,#6D707C)]">{DISCLAIMER}</p>
        </ShipSection>
        {!isLoading && (
          <>
            <ShipSection title="Your Address" action={editLink}>
              {address ? (
                <AddressLinesText address={address} />
              ) : (
                <SleekButton name="ShipAddAddressButton" onClick={() => setIsEditingAddress(true)}>
                  Add address +
                </SleekButton>
              )}
            </ShipSection>
            <ShipSection title="Delivery & Shipping Options">
              <ShippingOptionList
                services={draft.services}
                selectedId={draft.selectedServiceId}
                onSelect={draft.selectService}
                optionPrices={draft.fees.fees?.optionPrices ?? {}}
                originalOptionPrices={draft.fees.fees?.promo?.originalOptionPrices ?? {}}
                loading={draft.fees.loading}
                requiredCodeName={draft.requiredCodeName}
              />
              {draft.showHighValueDisclaimer && <HighValueDisclaimer />}
              {draft.isOptOut && (
                <ProtectionOptOutWarning acknowledged={draft.optOutAcknowledged} onAcknowledgedChange={draft.setOptOutAcknowledged} />
              )}
            </ShipSection>
            <ShipSection title="Card">
              <VaultCardRow item={item} />
            </ShipSection>
            <ShipSection title="Payment" isLast>
              <PaymentSummary
                shipping={shippingRowValue}
                insurance={insuranceRowValue}
                showInsurance={showInsuranceRow}
                handling={handlingFee}
                salesTax={salesTax}
                total={shownTotal}
                promo={promo}
              />
              <ShipCardFeedback
                feesError={draft.fees.error}
                onRetryFees={draft.fees.retry}
                fullQuota={draft.quotaFull ? draft.quota : null}
                error={draft.error}
              />
            </ShipSection>
          </>
        )}
      </main>
      {!isLoading && (
        <div className="mx-auto w-full max-w-[480px] px-5 pt-2 pb-6">
          <SleekButton name="ShippingPrimaryButton" onClick={() => void draft.submit()} disabled={!canConfirm} className="w-full">
            {draft.submitLoading ? 'Confirming…' : confirmLabel}
          </SleekButton>
        </div>
      )}
    </div>
  );
}
